<template>
  <div
    class="!pl-4 cursor-pointer flex flex-row items-center gap-3 !justify-between dropdown_item repository_item_row"
    :class="{ selected: isSelected || isDropdownOpen }"
    @click="$emit('click', repository)"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="flex flex-row items-center gap-2 truncate">
      <ThemeImage
        class="size-5"
        :icon="imageNames.PANEL_REPOSITORY"
        alt="panel_repository_icon"
      />

      <span class="text-sm color_666 font-normal truncate">
        {{ renderEntity(repository) || repository?.name }}
      </span>
    </div>

    <div
      class="flex flex-row items-center gap-1 repository_item_actions"
      :class="{ show: isDropdownOpen || isHovered }"
    >
      <div
        v-for="(action, index) in actions"
        :key="`${action?.id}_action_${index}`"
        @click.stop="handleActionClick(action)"
      >
        <a-dropdown
          v-if="action?.options?.length > 0"
          trigger="click"
          placement="bottomLeft"
          :get-popup-container="(trigger) => trigger.parentElement"
          @visibleChange="handleDropdownChange($event, action.id)"
        >
          <a-tooltip placement="top" color="var(--tooltip-bg)">
            <template #title>
              <span class="text-[13px] font-normal primary_color">
                {{ action?.title }}
              </span>
            </template>

            <div
              class="size-4 flex items-center justify-center cursor-pointer !rounded icon_wrapper"
              :class="{ selected: activeDropdown === action.id }"
            >
              <ThemeImage
                class="size-4"
                :icon="action?.icon"
                :alt="`${action?.id}_icon`"
              />
            </div>
          </a-tooltip>

          <template #overlay>
            <a-menu class="dropdown_menu">
              <a-menu-item
                v-for="(option, oIndex) in action?.options"
                :key="`${option?.id}_option_${oIndex}`"
                @click="handleOptionClick(option, repository)"
              >
                <div class="flex flex-row items-center gap-[6px]">
                  <ThemeImage
                    class="size-4"
                    :icon="option?.icon"
                    :alt="`${option?.id}_icon`"
                  />

                  <span class="text-sm primary_color">{{ option?.title }}</span>
                </div>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>

        <a-tooltip
          v-else-if="action?.tooltip"
          placement="top"
          color="var(--tooltip-bg)"
        >
          <template #title>
            <span class="text-[13px] font-normal primary_color">
              {{ action?.title }}
            </span>
          </template>

          <ThemeImage
            class="size-4"
            :icon="action?.icon"
            :alt="`${action?.id}_icon`"
          />
        </a-tooltip>
      </div>
    </div>

    <ConfirmationModal
      v-model:visible="showDeleteModal"
      title="Delete Repository"
      :message="`Are you sure you want to delete ${repository?.name} repository?`"
      :loading="isDeleting"
      type="delete"
      @confirm="handleDeleteConfirm"
      @cancel="showDeleteModal = false"
      confirm-text="Delete"
      loading-text="Deleting..."
    />

    <AddOrEditRepositoryModal
      source="repository_item_rename"
      :visible="showRenameModal"
      :repository="repository"
      :space="null"
      mode="edit"
      @update:visible="showRenameModal = $event"
    />

    <CustomModal
      v-model:visible="showSetupModal"
      width="560px"
      :title="gitInstructionTexts.TITLE"
      :sub_title="gitInstructionTexts.SUB_TITLE"
      :visible_sections="{
        header: true,
        body: true,
        footer: false,
      }"
    >
      <GitInstructions :repo="repository" :size="componentSizes.SMALL" />
    </CustomModal>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { message } from "ant-design-vue";
import { renderEntity } from "@/src/utils/utils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { componentSizes } from "@/src/constants/customComponentConstants.js";
import {
  getRepositoryItemActions,
  gitInstructionTexts,
  repositoryEvents,
} from "@/src/constants/codebaseConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import CodeService from "@/src/api/CodeService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import GitInstructions from "@/src/components/codebase_components/instructions/GitInstructions.vue";
import AddOrEditRepositoryModal from "@/src/components/spaces_components/repositories/AddOrEditRepositoryModal.vue";

const props = defineProps({
  repository: { type: Object, required: true },
  isSelected: { type: Boolean, default: false },
});

const emit = defineEmits(["click", "action-click", "option-click"]);

const activeDropdown = ref(null);
const isDropdownOpen = ref(false);
const isHovered = ref(false);
const showDeleteModal = ref(false);
const showRenameModal = ref(false);
const showSetupModal = ref(false);
const isDeleting = ref(false);

const actions = getRepositoryItemActions(imageNames, {
  onGetSetupInstructions: () => {
    showSetupModal.value = true;
  },
  onRename: () => {
    activeDropdown.value = null;
    isDropdownOpen.value = false;
    isHovered.value = false;
    showRenameModal.value = true;
  },
  onDelete: () => {
    activeDropdown.value = null;
    isDropdownOpen.value = false;
    isHovered.value = false;
    showDeleteModal.value = true;
  },
});

const handleMouseEnter = () => {
  try {
    isHovered.value = true;
  } catch (error) {
    console.error("An error occurred while handling mouse enter: ", error);
  }
};

const handleMouseLeave = () => {
  try {
    isHovered.value = false;
  } catch (error) {
    console.error("An error occurred while handling mouse leave: ", error);
  }
};

const handleActionClick = (action) => {
  try {
    if (!action?.options?.length && action?.action) {
      action.action();
    }

    emit("action-click", action);
  } catch (error) {
    console.error("An error occurred while handling action click: ", error);
  }
};

const handleOptionClick = (option, repository) => {
  try {
    if (option?.action) {
      option.action(repository);
    }
    emit("option-click", option, repository);
  } catch (error) {
    console.error("An error occurred while handling option click: ", error);
  }
};

const handleDropdownChange = (visible, actionId) => {
  try {
    if (visible) {
      activeDropdown.value = actionId;
      isDropdownOpen.value = true;
    } else {
      if (activeDropdown.value === actionId) {
        activeDropdown.value = null;
        isDropdownOpen.value = false;
      }
    }
  } catch (error) {
    console.error("An error occurred while handling dropdown change: ", error);
  }
};

const handleDeleteConfirm = async () => {
  try {
    isDeleting.value = true;
    await CodeService.base.repos.delete(props.repository?.id);
    message.success(
      `Repository ${props.repository?.name} deleted successfully!`,
    );
    showDeleteModal.value = false;
    eventBus.emit("repository-manipulated", repositoryEvents.DELETED);
  } catch (error) {
    console.error("An error occurred while deleting repository: ", error);
    message.error(error?.response?.data?.error || "Failed to delete repository");
  } finally {
    isDeleting.value = false;
  }
};
</script>

<style scoped>
.repository_item_actions {
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;
}

.repository_item_actions.show {
  opacity: 1;
  visibility: visible;
}
</style>
